import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, TrendingUp, PieChart, Users, Receipt, Calendar, Download, FileText, FileSpreadsheet } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';

const categoryColors = {
  food: '#f97316',
  transport: '#3b82f6',
  accommodation: '#a855f7',
  sports: '#22c55e',
  miscellaneous: '#6b7280',
};

const currencySymbols = {
  INR: '₹', USD: '$', EUR: '€', GBP: '£', AED: 'د.إ',
  SGD: 'S$', AUD: 'A$', CAD: 'C$', JPY: '¥',
};

export default function AnalyticsPage() {
  const { groupId } = useParams();
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [group, setGroup] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [groupId]);

  const fetchData = async () => {
    try {
      const [analyticsRes, groupRes] = await Promise.all([
        api.get(`/groups/${groupId}/analytics`),
        api.get(`/groups/${groupId}`)
      ]);
      setAnalytics(analyticsRes.data);
      setGroup(groupRes.data);
    } catch (error) {
      toast.error('Failed to load analytics');
      navigate('/groups');
    } finally {
      setLoading(false);
    }
  };

  const currency = analytics?.currency || group?.currency || 'INR';
  const symbol = currencySymbols[currency] || '₹';

  const formatCurrency = (amount) => {
    return `${symbol}${new Intl.NumberFormat('en-IN', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)}`;
  };

  const formatMonth = (monthStr) => {
    const [year, month] = monthStr.split('-');
    const date = new Date(year, parseInt(month) - 1);
    return date.toLocaleDateString('en-IN', { month: 'short' });
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/groups/${groupId}/export/csv`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${group?.name?.replace(/\s+/g, '_')}_expenses.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('CSV exported!');
    } catch (error) {
      toast.error('Failed to export CSV');
    } finally {
      setExporting(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const response = await api.get(`/groups/${groupId}/export/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${group?.name?.replace(/\s+/g, '_')}_report.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF exported!');
    } catch (error) {
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4" data-testid="analytics-loading">
        <Skeleton className="h-12 w-32" />
        <Skeleton className="h-40 rounded-2xl" />
        <Skeleton className="h-64 rounded-2xl" />
      </div>
    );
  }

  const maxMonthAmount = Math.max(...(analytics?.monthly_trends?.map(m => m.amount) || [1]));

  return (
    <div data-testid="analytics-page">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(`/groups/${groupId}`)}
          className="w-10 h-10 rounded-xl bg-card border-2 border-border flex items-center justify-center hover:bg-accent transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div className="flex-1">
          <h1 className="font-display text-xl font-bold">Analytics</h1>
          <p className="text-sm text-muted-foreground">{group?.name} • {currency}</p>
        </div>
      </div>

      {/* Export Buttons */}
      <div className="flex gap-3 mb-6">
        <Button
          variant="outline"
          onClick={handleExportCSV}
          disabled={exporting}
          data-testid="export-csv-btn"
          className="flex-1 rounded-xl border-2"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2" />
          Export CSV
        </Button>
        <Button
          variant="outline"
          onClick={handleExportPDF}
          disabled={exporting}
          data-testid="export-pdf-btn"
          className="flex-1 rounded-xl border-2"
        >
          <FileText className="w-4 h-4 mr-2" />
          Export PDF
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 gap-4 mb-6">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2 }}
        >
          <Card className="p-4 border-2 rounded-2xl" data-testid="total-expenses-card">
            <div className="flex items-center gap-2 mb-2">
              <Receipt className="w-4 h-4 text-primary" />
              <span className="text-xs text-muted-foreground uppercase tracking-wide">Total Spent</span>
            </div>
            <p className="font-display text-2xl font-bold amount-display">
              {formatCurrency(analytics?.total_expenses || 0)}
            </p>
          </Card>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.2, delay: 0.05 }}
        >
          <Card className="p-4 border-2 rounded-2xl" data-testid="avg-expense-card">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp className="w-4 h-4 text-primary" />
              <span className="text-xs text-muted-foreground uppercase tracking-wide">Avg Expense</span>
            </div>
            <p className="font-display text-2xl font-bold amount-display">
              {formatCurrency(analytics?.average_expense || 0)}
            </p>
          </Card>
        </motion.div>
      </div>

      {/* Expense Count Stats */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: 0.1 }}
      >
        <Card className="p-4 border-2 rounded-2xl mb-6">
          <h3 className="font-display font-bold mb-3 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-primary" />
            Expense Status
          </h3>
          <div className="grid grid-cols-3 gap-3">
            <div className="text-center p-2 rounded-xl bg-yellow-500/10">
              <p className="font-display text-xl font-bold text-yellow-600 dark:text-yellow-400">
                {analytics?.status_breakdown?.pending || 0}
              </p>
              <p className="text-xs text-muted-foreground">Pending</p>
            </div>
            <div className="text-center p-2 rounded-xl bg-green-500/10">
              <p className="font-display text-xl font-bold text-green-600 dark:text-green-400">
                {analytics?.status_breakdown?.approved || 0}
              </p>
              <p className="text-xs text-muted-foreground">Approved</p>
            </div>
            <div className="text-center p-2 rounded-xl bg-red-500/10">
              <p className="font-display text-xl font-bold text-red-600 dark:text-red-400">
                {analytics?.status_breakdown?.rejected || 0}
              </p>
              <p className="text-xs text-muted-foreground">Rejected</p>
            </div>
          </div>
        </Card>
      </motion.div>

      {/* Category Breakdown */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: 0.15 }}
      >
        <Card className="p-4 border-2 rounded-2xl mb-6" data-testid="category-breakdown">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2">
            <PieChart className="w-4 h-4 text-primary" />
            Category Breakdown
          </h3>
          
          {analytics?.category_breakdown?.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No expenses yet</p>
          ) : (
            <div className="space-y-3">
              {analytics?.category_breakdown?.map((cat, index) => (
                <div key={cat.category} className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="capitalize font-medium">{cat.category}</span>
                    <span className="font-display font-bold amount-display">
                      {formatCurrency(cat.amount)}
                    </span>
                  </div>
                  <div className="h-3 bg-muted rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: `${cat.percentage}%` }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      className="h-full rounded-full"
                      style={{ backgroundColor: categoryColors[cat.category] || '#6b7280' }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground">{cat.percentage}%</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      </motion.div>

      {/* Monthly Trends */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: 0.2 }}
      >
        <Card className="p-4 border-2 rounded-2xl mb-6" data-testid="monthly-trends">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-primary" />
            Monthly Trends
          </h3>
          
          <div className="flex items-end justify-between gap-2 h-32">
            {analytics?.monthly_trends?.map((month, index) => (
              <div key={month.month} className="flex-1 flex flex-col items-center gap-1">
                <motion.div
                  initial={{ height: 0 }}
                  animate={{ 
                    height: maxMonthAmount > 0 
                      ? `${(month.amount / maxMonthAmount) * 100}%` 
                      : '0%' 
                  }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="w-full bg-primary rounded-t-lg min-h-[4px]"
                  style={{ maxHeight: '100%' }}
                />
                <span className="text-xs text-muted-foreground">{formatMonth(month.month)}</span>
              </div>
            ))}
          </div>
        </Card>
      </motion.div>

      {/* Top Spenders */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.2, delay: 0.25 }}
      >
        <Card className="p-4 border-2 rounded-2xl" data-testid="top-spenders">
          <h3 className="font-display font-bold mb-4 flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            Top Spenders
          </h3>
          
          {analytics?.top_spenders?.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No expenses yet</p>
          ) : (
            <div className="space-y-3">
              {analytics?.top_spenders?.map((spender, index) => (
                <div key={spender.user_id} className="flex items-center justify-between p-3 rounded-xl bg-card border-2 border-border">
                  <div className="flex items-center gap-3">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center font-display font-bold text-sm ${
                      index === 0 ? 'bg-yellow-500/20 text-yellow-600' :
                      index === 1 ? 'bg-gray-400/20 text-gray-600' :
                      index === 2 ? 'bg-orange-500/20 text-orange-600' :
                      'bg-muted text-muted-foreground'
                    }`}>
                      {index + 1}
                    </div>
                    <span className="font-medium">{spender.name}</span>
                  </div>
                  <span className="font-display font-bold amount-display">
                    {formatCurrency(spender.amount)}
                  </span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </motion.div>
    </div>
  );
}
